import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import DashboardLayout from "../../pages/dashboard/DashboardLayout";
import API from "../../api/api";

export default function EditArticle() {
    const { slug } = useParams();
    const navigate = useNavigate();

    const [form, setForm] = useState({
        title: "",
        content: "",
        sport: "",
        category: "",
        region: "global",
        tags: "",
        images: "",
        image_credit: "", // ✅ ADDED
        seo_title: "",
        meta_description: "",
        is_live: false,
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    /* ================= FETCH ARTICLE ================= */
    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError("");

        API.get(`/api/articles/${slug}`)
            .then((res) => {
                if (!isMounted) return;

                const a = res.data;

                setForm({
                    title: a.title || "",
                    content: a.content || "",
                    sport: a.sport || "",
                    category: a.category || "",
                    region: a.region || "global",
                    tags: Array.isArray(a.tags) ? a.tags.join(", ") : "",
                    images: a.images || "",
                    image_credit: a.image_credit || "", // ✅ LOAD
                    seo_title: a.seo_title || "",
                    meta_description: a.meta_description || "",
                    is_live: Boolean(a.is_live),
                });

                setLoading(false);
            })
            .catch(() => {
                if (!isMounted) return;
                setError("Failed to load article");
                setLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [slug]);

    /* ================= HANDLE CHANGE ================= */
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    /* ================= SUBMIT UPDATE ================= */
    const submit = async () => {
        try {
            await API.put(`/api/articles/${slug}`, {
                ...form,
                tags: form.tags
                    ? form.tags.split(",").map((t) => t.trim())
                    : [],
            });

            // Clear cached widgets
            if (window) {
                window.__POPULAR_CACHE__ = {};
                window.__LATEST_CACHE__ = null;
            }

            alert("Article updated successfully 🏆");
            navigate("/");
        } catch (err) {
            console.error(err);
            setError("Update failed. Please try again.");
        }
    };

    return (
        <DashboardLayout>
            <div className="max-w-5xl mx-auto px-6 py-10 bg-gray-50">

                {/* PAGE HEADER */}
                <div className="mb-8 border-l-4 border-red-600 pl-4">
                    <h1 className="text-3xl font-extrabold text-gray-900">
                        ✏️ Edit Sports Article
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Update match news, analysis or highlights
                    </p>
                </div>

                <div className="bg-white shadow rounded-xl p-6 space-y-6 border border-red-200">

                    {loading && (
                        <p className="text-gray-500 text-sm">Loading article…</p>
                    )}

                    {error && (
                        <p className="text-red-600 text-sm">{error}</p>
                    )}

                    {!loading && (
                        <>
                            {/* TITLE */}
                            <div>
                                <label className="block text-sm font-semibold mb-1">
                                    Article Title
                                </label>
                                <input
                                    name="title"
                                    value={form.title}
                                    onChange={handleChange}
                                    className="w-full border p-2 rounded focus:ring-2 focus:ring-red-400"
                                />
                            </div>

                            {/* CONTENT */}
                            <div>
                                <label className="block text-sm font-semibold mb-1">
                                    Article Content
                                </label>
                                <textarea
                                    name="content"
                                    value={form.content}
                                    onChange={handleChange}
                                    className="w-full border p-3 rounded h-56 focus:ring-2 focus:ring-red-400"
                                />
                            </div>

                            {/* SPORT */}
                            <div>
                                <label className="block text-sm font-semibold mb-1">
                                    Sport
                                </label>
                                <input
                                    name="sport"
                                    value={form.sport.replace(/-/g, " ")}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            sport: e.target.value
                                                .trim()
                                                .toLowerCase()
                                                .replace(/\s+/g, "-"),
                                        }))
                                    }
                                    className="w-full border p-2 rounded focus:ring-2 focus:ring-red-400"
                                />
                            </div>

                            {/* CATEGORY */}
                            <div>
                                <label className="block text-sm font-semibold mb-1">
                                    Category
                                </label>
                                <select
                                    name="category"
                                    value={form.category}
                                    onChange={handleChange}
                                    className="w-full border p-2 rounded focus:ring-2 focus:ring-red-400"
                                >
                                    <option value="">Select Category</option>
                                    <option value="trending">Trending</option>
                                    <option value="breaking-news">Breaking News</option>
                                    <option value="analysis">Analysis</option>
                                    <option value="highlights">Highlights</option>
                                </select>
                            </div>

                            {/* REGION */}
                            <div>
                                <label className="block text-sm font-semibold mb-1">
                                    Region
                                </label>
                                <select
                                    name="region"
                                    value={form.region}
                                    onChange={handleChange}
                                    className="w-full border p-2 rounded focus:ring-2 focus:ring-red-400"
                                >
                                    <option value="global">Global</option>
                                    <option value="india">India</option>
                                    <option value="uk">UK</option>
                                    <option value="usa">USA</option>
                                </select>
                            </div>

                            {/* TAGS */}
                            <div>
                                <label className="block text-sm font-semibold mb-1">
                                    Tags
                                </label>
                                <input
                                    name="tags"
                                    value={form.tags}
                                    onChange={handleChange}
                                    className="w-full border p-2 rounded focus:ring-2 focus:ring-red-400"
                                />
                            </div>

                            {/* FEATURED IMAGE */}
                            <div>
                                <label className="block text-sm font-semibold mb-1">
                                    Featured Image URL
                                </label>
                                <input
                                    name="images"
                                    value={form.images}
                                    onChange={handleChange}
                                    className="w-full border p-2 rounded focus:ring-2 focus:ring-red-400"
                                />
                            </div>

                            {/* ✅ IMAGE CREDIT (NEW & VISIBLE) */}
                            <div>
                                <label className="block text-sm font-semibold mb-1">
                                    Image Credit
                                </label>
                                <input
                                    name="image_credit"
                                    value={form.image_credit}
                                    onChange={handleChange}
                                    placeholder="e.g. Miami Hurricanes Football / X"
                                    className="w-full border p-2 rounded focus:ring-2 focus:ring-red-400"
                                />
                            </div>

                            {/* SEO TITLE */}
                            <div>
                                <label className="block text-sm font-semibold mb-1">
                                    SEO Title
                                </label>
                                <input
                                    name="seo_title"
                                    value={form.seo_title}
                                    onChange={handleChange}
                                    className="w-full border p-2 rounded focus:ring-2 focus:ring-red-400"
                                />
                            </div>

                            {/* META DESCRIPTION */}
                            <div>
                                <label className="block text-sm font-semibold mb-1">
                                    Meta Description
                                </label>
                                <textarea
                                    name="meta_description"
                                    value={form.meta_description}
                                    onChange={handleChange}
                                    className="w-full border p-2 rounded h-28 focus:ring-2 focus:ring-red-400"
                                />
                            </div>

                            {/* LIVE TOGGLE */}
                            <div className="flex items-center gap-3 pt-2">
                                <input
                                    type="checkbox"
                                    name="is_live"
                                    checked={form.is_live}
                                    onChange={handleChange}
                                    className="w-4 h-4 accent-red-600"
                                />
                                <label className="text-sm font-semibold">
                                    Publish Article (Live)
                                </label>
                            </div>

                            {/* ACTION */}
                            <div className="pt-4">
                                <button
                                    onClick={submit}
                                    className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition"
                                >
                                    Update Article
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
}
